import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type { Router } from 'vue-router'

/** Query param: opens Live Class page in a mocked UI state (dev-only). */
export const LIVE_CLASS_DEV_SCENARIO_QUERY = 'devLiveClassScenario'

const LIVE_CLASS_DEV_SCENARIO_STORAGE_KEY = 'live_class_dev_scenario'

/**
 * Every entry matches a real customer-visible state or failure path on LiveClassPage
 * (or the same copy/messages surfaced via useLiveClassZoomSession).
 */
export const LIVE_CLASS_DEV_SCENARIO_IDS = [
  'preparing',
  'joining',
  'ready',
  'class_not_found',
  'not_joinable',
  'zoom_app_fallback_browser',
  'zoom_app_fallback_exhausted',
  'zoom_app_fallback_sdk_3603',
  'zoom_app_fallback_sdk_3608',
  'zoom_app_fallback_sdk_3611',
  'internal_prepare_exhausted',
  'prepare_failed_retry',
  'subscription_expired',
  'join_failed',
  'sdk_init_failed',
  'sdk_join_failed',
  'join_link_not_ready',
  'asset_error',
  'asset_error_pre_embed_exhausted',
  'permission_error',
  'permission_error_pre_embed_exhausted',
] as const

export type LiveClassDevScenarioId = (typeof LIVE_CLASS_DEV_SCENARIO_IDS)[number]

export const LIVE_CLASS_DEV_SCENARIOS: ReadonlyArray<{
  id: LiveClassDevScenarioId
  label: string
  description: string
}> = [
  {
    id: 'preparing',
    label: 'Preparing (loading)',
    description: 'While the webinar loads, registration runs, and the SDK prepares.',
  },
  {
    id: 'joining',
    label: 'Joining (loading)',
    description: 'After Join is tapped, until Zoom reports the user is in the meeting.',
  },
  {
    id: 'ready',
    label: 'Ready to join',
    description: 'Prepare finished; user should tap Join Class.',
  },
  {
    id: 'class_not_found',
    label: 'Class not found',
    description: 'Class instance does not exist (invalid or mistyped URL). Shows message and redirects to home.',
  },
  {
    id: 'not_joinable',
    label: 'Class not joinable',
    description: 'Class exists but has ended or is outside the join window. Shows message and redirects to home.',
  },
  {
    id: 'zoom_app_fallback_browser',
    label: 'Zoom app fallback — browser / features',
    description:
      'After successful Zoom registration: feature check fails (computer audio/video not supported in embedded web).',
  },
  {
    id: 'zoom_app_fallback_exhausted',
    label: 'Zoom app fallback — embed retries exhausted',
    description: 'Several failed attempts on the embedded path while past registration.',
  },
  {
    id: 'zoom_app_fallback_sdk_3603',
    label: 'Zoom app fallback — SDK 3603',
    description: 'Zoom join error: meeting cannot be completed in the browser (code 3603).',
  },
  {
    id: 'zoom_app_fallback_sdk_3608',
    label: 'Zoom app fallback — SDK 3608',
    description: 'Zoom join error: browser + current audio setup (code 3608).',
  },
  {
    id: 'zoom_app_fallback_sdk_3611',
    label: 'Zoom app fallback — SDK 3611',
    description: 'Zoom join error: browser join not allowed for this meeting (code 3611).',
  },
  {
    id: 'internal_prepare_exhausted',
    label: 'Prepare exhausted (before embed)',
    description: 'Retries exhausted during registration / early prepare — generic error, not Zoom-app pitch.',
  },
  {
    id: 'prepare_failed_retry',
    label: 'Prepare failed — try again',
    description: 'First prepare failures with retries left (same copy as prepareClassWithErrorHandling).',
  },
  {
    id: 'subscription_expired',
    label: 'Subscription ended',
    description: 'Signature step when subscription is not active.',
  },
  {
    id: 'join_failed',
    label: 'Join threw (sync)',
    description: 'Rare: exception from joinZoom before async SDK callback (same copy as join catch).',
  },
  {
    id: 'sdk_init_failed',
    label: 'SDK init failed',
    description: 'ZoomMtg.init error — same “Join with the Zoom app” screen as browser/features fallback.',
  },
  {
    id: 'sdk_join_failed',
    label: 'SDK join failed (generic)',
    description:
      'ZoomMtg.join error (other than web-not-allowed codes) — same Zoom app fallback layout.',
  },
  {
    id: 'join_link_not_ready',
    label: 'Join link not ready',
    description: 'User used Zoom-app actions before a join URL was available.',
  },
  {
    id: 'asset_error',
    label: 'Chunk / asset load',
    description: 'First-chunk or asset errors while preparing (retries left).',
  },
  {
    id: 'asset_error_pre_embed_exhausted',
    label: 'Asset load — exhausted before embed',
    description: 'Asset errors with retries exhausted before embedded path.',
  },
  {
    id: 'permission_error',
    label: 'Permission blocked',
    description: 'Browser blocked mic/camera/gesture, first attempts.',
  },
  {
    id: 'permission_error_pre_embed_exhausted',
    label: 'Permission — exhausted before embed',
    description: 'Permission errors with retries exhausted before embedded path.',
  },
]

export function isLiveClassDevUiEnabled(): boolean {
  return (
    import.meta.env.DEV || import.meta.env.VITE_ENABLE_LIVE_CLASS_DEV_UI === 'true'
  )
}

export function parseLiveClassDevScenario(raw: string | null | undefined): LiveClassDevScenarioId | null {
  if (!raw) return null
  return LIVE_CLASS_DEV_SCENARIO_IDS.includes(raw as LiveClassDevScenarioId)
    ? (raw as LiveClassDevScenarioId)
    : null
}

/** Prefer URL query; then session (set from Dev Settings before navigation). */
export function readLiveClassDevScenario(
  route: RouteLocationNormalizedLoaded,
): LiveClassDevScenarioId | null {
  const q = route.query[LIVE_CLASS_DEV_SCENARIO_QUERY]
  const fromQuery = typeof q === 'string' ? parseLiveClassDevScenario(q) : null
  if (fromQuery) return fromQuery
  if (typeof sessionStorage === 'undefined') return null
  const stored = sessionStorage.getItem(LIVE_CLASS_DEV_SCENARIO_STORAGE_KEY)
  return parseLiveClassDevScenario(stored)
}

export function persistLiveClassDevScenarioForNavigation(id: LiveClassDevScenarioId): void {
  sessionStorage.setItem(LIVE_CLASS_DEV_SCENARIO_STORAGE_KEY, id)
}

export function clearLiveClassDevScenarioStorage(): void {
  sessionStorage.removeItem(LIVE_CLASS_DEV_SCENARIO_STORAGE_KEY)
}

export function clearLiveClassDevScenarioFromRoute(
  router: Router,
  route: RouteLocationNormalizedLoaded,
): void {
  clearLiveClassDevScenarioStorage()
  if (!(LIVE_CLASS_DEV_SCENARIO_QUERY in route.query)) return
  const nextQuery = { ...route.query }
  delete nextQuery[LIVE_CLASS_DEV_SCENARIO_QUERY]
  void router.replace({ path: route.path, query: nextQuery })
}
